import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { amazonConnectConfigs } from '../../constants';
import { sciBrandNameSelector } from '../shared/SciPage/SciPage.selector';

export function AmazonConnectLiveChatScript() {
    const [scriptLoaded, setScriptLoaded] = useState(false);
    const isProduction = process.env.NODE_ENV === 'production';
    const brandName = useSelector(sciBrandNameSelector);
    const config = brandName ? amazonConnectConfigs[brandName] : null;

    useEffect(() => {
        window.amazon_connect =
            window.amazon_connect ||
            function (...args: unknown[]) {
                (window.amazon_connect as unknown as { ac: unknown[] }).ac =
                    (window.amazon_connect as unknown as { ac: unknown[] }).ac || [];
                (window.amazon_connect as unknown as { ac: unknown[] }).ac.push(args);
            };
    }, []);

    const handleScriptLoad = useCallback(() => {
        if (!config?.enabled) {
            return;
        }

        if (!config.snippetId) {
            console.warn('Amazon Connect Chat: Missing snippetId in configuration');
            return;
        }

        if (window.amazon_connect) {
            const styles = {
                iconType: config.styles?.iconType || 'CHAT',
                openChat: {
                    color: config.styles?.openChat?.color || '#ffffff',
                    backgroundColor: config.styles?.openChat?.backgroundColor || '#B3232C',
                    ...config.styles?.openChat,
                },
                closeChat: {
                    color: config.styles?.closeChat?.color || '#ffffff',
                    backgroundColor: config.styles?.closeChat?.backgroundColor || '#B3232C',
                    ...config.styles?.closeChat,
                },
            };

            window.amazon_connect('styles', styles);
            window.amazon_connect('snippetId', config.snippetId);
            window.amazon_connect('supportedMessagingContentTypes', [
                'text/plain',
                'application/vnd.amazonaws.connect.message.interactive',
                'application/vnd.amazonaws.connect.message.interactive.response',
            ]);
            window.amazon_connect('contactAttributes', {page_url: window.location.href});
            window.amazon_connect('customerDisplayName', function(callback: any) {
                const displayName = 'Guest';
                callback(displayName);
            });
        }
    }, [config]);

    useEffect(() => {
        if (!config?.enabled) {
            return;
        }

        if (!isProduction && !config.loadInDevelopment) {
            return;
        }

        if (!config.scriptUrl || !config.widgetId) {
            console.warn('Amazon Connect Chat: Missing scriptUrl or widgetId in configuration');
            return;
        }

        const existingScript = document.getElementById(config.widgetId);
        if (existingScript) {
            if (scriptLoaded) {
                handleScriptLoad();
            }
            return;
        }

        const script = document.createElement('script');
        script.id = config.widgetId;
        script.src = config.scriptUrl;
        script.async = true;

        script.onload = () => {
            setScriptLoaded(true);
            handleScriptLoad();
        };

        script.onerror = () => {
            console.log('Errors occured on loading Amazon Connect Chat script');
        };

        document.body.appendChild(script);

        return () => {
            const scriptElement = document.getElementById(config.widgetId);
            if (scriptElement) {
                document.body.removeChild(scriptElement);
            }
        };
    }, [config, isProduction, scriptLoaded, handleScriptLoad]);

    return null;
}
